import {
    Box,
    Snackbar,
    Alert,
    IconButton,
    useTheme,
    useMediaQuery,
    Tooltip,
} from "@mui/material";
import { useState, useContext } from "react";
import { ColorModeContext } from "../theme";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import FeedbackIcon from "@mui/icons-material/Feedback";
import { useLocation, useNavigate } from "react-router-dom";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";
import GlobalSearch from "./SearchBox";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FiltersContext } from "../store/filters-options-context";

export default function Header() {
    const theme = useTheme();
    const colorMode = useContext(ColorModeContext);
    const location = useLocation();
    const mainCat = location.pathname.split("/")[1];

    const [copy, setCopy] = useState({
        open: false,
        vertical: "top",
        horizontal: "center",
    });
    const { vertical, horizontal, open } = copy;

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const navigate = useNavigate();
    const { activeMenu } = useContext(FiltersContext);

    const handleBack = () => {
        navigate(-1); // goes back to previous page
    };

    const isResourcesPage = location.pathname === "/resources";

    const handleResourcesClick = () => {
        // Navigate to resources with the current location as state
        navigate("/resources", { state: { from: location } });
    };

    const handleClick = (newState) => () => {
        // Build a share URL from the current path (HashRouter). indicators URLs are
        // indicators/:section/:subCat/:countryId/:years/:scroll? - override the
        // trailing scroll segment with the live activeMenu value for accuracy.
        const isIndicators = location.pathname.startsWith('/indicators/');
        let path = location.pathname;

        if (isIndicators && activeMenu != null && String(activeMenu) !== '') {
            const parts = path.split('/').filter(Boolean);
            if (parts.length >= 5) {
                parts[5] = encodeURIComponent(String(activeMenu));
                path = `/${parts.slice(0, 6).join('/')}`;
            }
        }

        const url = `${window.location.origin}${window.location.pathname}#${path}`;
        navigator.clipboard.writeText(url);
        setCopy({ ...newState, open: true });
    };

    const handleClose = () => {
        setCopy({ ...copy, open: false });
    };

    return (
        <header className="header">
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                    component="img"
                    src={
                        theme.palette.mode === "light"
                            ? require("../assets/logo-en.svg").default
                            : require("../assets/logo-en-white.svg").default
                    }
                    alt="FAO logo"
                    sx={{
                        height: { xs: "25px", md: "40px" },
                        marginLeft: { xs: location.pathname !== "/" && "48px", md: "0" },
                        cursor: "pointer",
                    }}
                    onClick={() => navigate("/")}
                />
                {!isMobile && location.pathname !== "/" && (
                    <Tooltip title="Go to Home Page" arrow>
                        <Box
                            onClick={() => navigate("/")}
                            sx={{
                                pl: "9px",
                                ml: "12px",
                                lineHeight: "1.6rem",
                                letterSpacing: "-0.2pt",
                                color: theme.palette.primary.fontColor,
                                textDecoration: "none",
                                fontWeight: "bold",
                                fontSize: { md: "10px", xl: "11px" },
                                borderLeft: `1px solid #cbcbcb`,
                                cursor: "pointer",
                            }}
                        >
                            THE STATE OF AGRIFOOD SYSTEMS
                        </Box>
                    </Tooltip>
                )}
            </Box>
            <Box display="flex" flexDirection="row" sx={{ alignItems: "center" }}>
                {!isMobile &&
                    location.pathname !== "/" &&
                    mainCat &&
                    (mainCat === "charts" || mainCat === "indicators") ? (
                    <GlobalSearch />
                ) : null}
                <Box>
                    {isResourcesPage && (
                        <Tooltip title="Go Back" arrow>
                            <IconButton
                                onClick={handleBack}
                                sx={{
                                    alignItems: "center",
                                    color: theme.palette.primary.fontColor,
                                    textTransform: "none",
                                    mr: 1,
                                    ml: 1,
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={theme.palette.mode === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"} arrow>
                        <IconButton
                            onClick={colorMode.toggleColorMode}
                            sx={{ ml: 1, color: theme.palette.primary.fontColor }}
                        >
                            {theme.palette.mode === "light" ? (
                                <DarkModeOutlined />
                            ) : (
                                <LightModeOutlined />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Share The Current Chart" arrow>
                        <IconButton
                            sx={{
                                alignItems: "center",
                                color: theme.palette.primary.fontColor,
                                textTransform: "none",
                                mr: 1,
                                ml: 1,
                            }}
                            onClick={handleClick({ vertical: "top", horizontal: "center" })}
                        >
                            <ShareIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isResourcesPage ? "Already on Resources page" : "Resources & Information"} arrow>
                        <span>
                            <IconButton
                                onClick={handleResourcesClick}
                                disabled={isResourcesPage}
                                sx={{
                                    alignItems: "center",
                                    color: theme.palette.primary.fontColor,
                                    textTransform: "none",
                                    mr: 1,
                                    ml: 1,
                                    opacity: isResourcesPage ? 0.5 : 1,
                                    cursor: isResourcesPage ? "not-allowed" : "pointer",
                                }}
                            >
                                <InfoOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Send Feedback" arrow>
                        <IconButton
                            component="a"
                            href="mailto:FAO-Statistics@fao.org?subject=AFS Data Platform Feedback"
                            sx={{
                                alignItems: "center",
                                color: theme.palette.primary.fontColor,
                                textTransform: "none",
                                mr: 1,
                                ml: 1,
                            }}
                            aria-label="Send feedback"
                        >
                            <FeedbackIcon />
                        </IconButton>
                    </Tooltip>
                    <Snackbar
                        anchorOrigin={{ vertical, horizontal }}
                        open={open}
                        onClose={handleClose}
                        autoHideDuration={3000}
                        key={vertical + horizontal}
                    >
                        <Alert
                            className="alert-clipboard"
                            severity="success"
                            icon={<LinkIcon fontSize="inherit" />}
                        >
                            Link copied to clipboard!
                        </Alert>
                    </Snackbar>
                </Box>
            </Box>
        </header>
    );
}
